import React, { useEffect, useMemo, useState } from 'react';
import { shopApi } from '../../utils/shopApi';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Save, Search, RefreshCw, Sheet as SheetIcon, AlertTriangle } from 'lucide-react';

/**
 * Master bulk-edit table for the shop inventory. Edit `current_stock`,
 * `online_price`, `pos_price`, `base_price` and `is_visible_online` inline
 * for many products at once, then hit "Save changes" to PATCH them all in
 * a single request. Rows with unsaved changes are highlighted amber.
 */
export default function ShopProductsBulkEditPage() {
  const [products, setProducts] = useState([]);
  const [edits, setEdits] = useState({}); // { id: {field: value, ...} }
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await shopApi.get('/shop/admin/products');
      setProducts(Array.isArray(r.data) ? r.data : (r.data.items || []));
      setEdits({});
    } catch (err) {
      toast.error('Failed to load shop products');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.sku || '').toLowerCase().includes(q) ||
      (p.category || '').toLowerCase().includes(q)
    );
  }, [products, search]);

  const setField = (id, field, value) => setEdits((prev) => ({
    ...prev,
    [id]: { ...(prev[id] || {}), [field]: value },
  }));

  const cellValue = (p, field) => {
    if (edits[p.id] && field in edits[p.id]) return edits[p.id][field];
    return p[field] ?? '';
  };

  const dirtyCount = Object.keys(edits).length;
  const lowStock = filtered.filter((p) => Number(cellValue(p, 'current_stock')) < 10).length;

  const save = async () => {
    if (dirtyCount === 0) return;
    setSaving(true);
    try {
      const updates = Object.entries(edits).map(([id, changes]) => {
        const row = { id };
        for (const [k, v] of Object.entries(changes)) {
          if (v === '' || v === null || v === undefined) continue;
          if (k === 'is_visible_online') row[k] = !!v;
          else row[k] = Number(v);
        }
        return row;
      });
      const r = await shopApi.patch('/shop/admin/products/bulk', { updates });
      toast.success(`Saved · ${r.data.updated} product${r.data.updated === 1 ? '' : 's'} updated`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk save failed');
    } finally { setSaving(false); }
  };

  const discardAll = () => setEdits({});

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-serif font-bold flex items-center gap-2">
            <SheetIcon className="w-7 h-7 text-primary" /> Bulk stock &amp; prices
          </h1>
          <p className="text-sm text-muted-foreground">Edit stock and prices for many products at once. Yellow rows have unsaved changes.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={load} disabled={loading || saving} data-testid="bulk-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={discardAll} disabled={!dirtyCount || saving} data-testid="bulk-discard">
            Discard {dirtyCount ? `(${dirtyCount})` : ''}
          </Button>
          <Button size="sm" onClick={save} disabled={!dirtyCount || saving} data-testid="bulk-save">
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Saving…' : `Save changes${dirtyCount ? ` (${dirtyCount})` : ''}`}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 bg-white border border-border rounded-xl p-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search by name, SKU or category…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="bulk-search" />
        </div>
        {lowStock > 0 && (
          <div className="text-xs px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 inline-flex items-center gap-1.5" data-testid="bulk-low-stock-warn">
            <AlertTriangle className="w-3.5 h-3.5" /> {lowStock} low-stock item{lowStock === 1 ? '' : 's'}
          </div>
        )}
        <div className="text-xs text-muted-foreground">{filtered.length} of {products.length} products</div>
      </div>

      <div className="bg-white border border-border rounded-xl overflow-x-auto" data-testid="bulk-table-wrapper">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Product</th>
              <th className="px-3 py-2 text-left">Category</th>
              <th className="px-3 py-2 text-right">Stock</th>
              <th className="px-3 py-2 text-right">Base RWF</th>
              <th className="px-3 py-2 text-right">POS RWF</th>
              <th className="px-3 py-2 text-right">Online RWF</th>
              <th className="px-3 py-2 text-center">Visible online</th>
            </tr>
          </thead>
          <tbody data-testid="bulk-table-body">
            {loading && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No products match.</td></tr>}
            {filtered.map((p) => {
              const dirty = !!edits[p.id];
              const rowCls = dirty ? 'bg-amber-50/60' : '';
              return (
                <tr key={p.id} className={`border-t border-border ${rowCls}`} data-testid={`bulk-row-${p.id}`}>
                  <td className="px-3 py-2 min-w-[220px]">
                    <div className="font-medium truncate">{p.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{p.sku || '—'} · {p.unit || 'unit'}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{p.category || '—'}</td>
                  <td className="px-3 py-2">
                    <Input
                      type="number" min={0} step="0.01"
                      value={cellValue(p, 'current_stock')}
                      onChange={(e) => setField(p.id, 'current_stock', e.target.value)}
                      className={`h-8 w-24 text-right ${Number(cellValue(p, 'current_stock')) < 10 ? 'text-amber-700 font-semibold' : ''}`}
                      data-testid={`bulk-stock-${p.id}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" min={0} step="1"
                      value={cellValue(p, 'base_price')}
                      onChange={(e) => setField(p.id, 'base_price', e.target.value)}
                      className="h-8 w-24 text-right" data-testid={`bulk-base-${p.id}`} />
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" min={0} step="1"
                      value={cellValue(p, 'pos_price')}
                      onChange={(e) => setField(p.id, 'pos_price', e.target.value)}
                      className="h-8 w-24 text-right" data-testid={`bulk-pos-${p.id}`} />
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" min={0} step="1"
                      value={cellValue(p, 'online_price')}
                      onChange={(e) => setField(p.id, 'online_price', e.target.value)}
                      className="h-8 w-24 text-right" data-testid={`bulk-online-${p.id}`} />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Switch
                      checked={!!cellValue(p, 'is_visible_online')}
                      onCheckedChange={(v) => setField(p.id, 'is_visible_online', v)}
                      data-testid={`bulk-visible-${p.id}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
